import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Simplified sticky header for A/B test variant B
 * - Logo only (no navigation distractions)
 * - Persistent CTA button
 * - Shows CTA after scrolling past hero
 */
const StickyHeaderB = ({ onCTAClick }) => {
  const navigate = useNavigate();
  const [showCTA, setShowCTA] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Show CTA after scrolling 300px (past hero)
    setShowCTA(currentScrollY > 300);

    // Hide/show header based on scroll direction
    if (currentScrollY < 10) {
      setIsVisible(true);
    } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setIsVisible(false);
    } else if (currentScrollY < lastScrollY) {
      setIsVisible(true);
    }

    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <header
      className={`bg-white border-b border-gray-200 sticky top-0 z-50 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
        <div className="flex items-center justify-between">
          {/* Logo - clickable to home */}
          <div
            className="flex items-center cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img
              src="/logo.png"
              alt="Anti-Recouvrement.com"
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </div>

          {/* Right side : lien Blog discret + CTA */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Lien Blog — texte discret pour ne pas concurrencer le CTA mais permettre l'accès SEO */}
            <Link
              to="/blog"
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-blue-900 transition-colors px-2 py-1"
              aria-label="Blog"
            >
              <BookOpen size={16} />
              <span className="hidden sm:inline">Blog</span>
            </Link>

            {/* CTA Button - apparaît après le scroll */}
            <div
              className={`transition-all duration-300 ${
                showCTA ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4 pointer-events-none'
              }`}
            >
              <Button
                onClick={onCTAClick}
                size="sm"
                className="px-4 py-2 text-sm font-semibold rounded-full shadow-md hover:shadow-lg transition-all"
                style={{ backgroundColor: '#1e3a8a', color: 'white' }}
              >
                <span className="hidden sm:inline">Diagnostic gratuit</span>
                <span className="sm:hidden">Analyser</span>
                <ArrowRight className="ml-1 sm:ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default StickyHeaderB;
